// AWS-hosted PostgREST client for moreece.com.
//
// After the Phase 3 Supabase retirement (2026-04-22) this is the only
// database client in the app — lib/supabase.js has been deleted and the
// Supabase package has been removed from dependencies.
//
// API surface matches supabase-js's `.from()` chain because we use
// @supabase/postgrest-js under the hood:
//   import { aws } from '../lib/awsdb';
//   const { data, error } = await aws.from('tasks').select('*').eq('status', 'open');
//
// Tier 3 (2026-05-01): JWT injection via custom fetch. After login the
// session JWT is held in module-local state; the wrapped fetch attaches
// `Authorization: Bearer <jwt>` on every request. PostgREST verifies it,
// exposes claims via current_setting('request.jwt.claims'), and RLS
// policies (e.g. tasks_owner_or_admin) decide row visibility.

import { PostgrestClient } from '@supabase/postgrest-js';

const url = import.meta.env.VITE_AWS_API_URL;
const apiKey = import.meta.env.VITE_AWS_API_KEY;

if (!url) {
  // Fail loudly at startup so misconfigured deploys are obvious.
  throw new Error(
    '[awsdb] VITE_AWS_API_URL is not set. ' +
    'Add it to app/.env.local for local dev, or to the frontend build env for deploys. ' +
    'See app/.env.example for format.'
  );
}

if (!apiKey) {
  // The PostgREST CloudFront sits behind a WAF that requires this header.
  // Fail at module load — better than every request 403ing later.
  throw new Error(
    '[awsdb] VITE_AWS_API_KEY is not set. ' +
    'Add it to app/.env.local for local dev, or to the frontend build env for deploys. ' +
    'See app/.env.example for format.'
  );
}

// Module-local JWT. setJwt() is called from the auth flow (after login)
// and from the App.jsx boot path (rehydrating a stored session).
let _jwt = null;

export function setJwt(jwt) {
  _jwt = jwt || null;
}

export function getJwt() {
  return _jwt;
}

// Custom fetch that re-reads _jwt on every call. We mutate this module's
// state (not the PostgrestClient's headers object) because the postgrest-js
// header object is copied into each new query builder at construction time;
// later mutations would not propagate. A custom fetch sidesteps that.
//
// Headers normalization (2026-05-06): postgrest-js passes init.headers as a
// `Headers` instance, not a plain object. Spreading a Headers instance with
// `{ ...init.headers }` produces an empty object — every header postgrest-js
// set (including `Content-Type: application/json`) is silently dropped.
// fetch then defaults the Content-Type to `text/plain;charset=UTF-8`,
// PostgREST cannot parse the JSON body, fails to match the function args,
// and returns PGRST202 "Could not find the function ... in the schema cache".
// The error message is misleading — the function IS in the cache; the body
// just couldn't be parsed. Use the Headers API to handle Headers instances,
// plain objects, and undefined consistently.
const jwtFetch = (input, init = {}) => {
  const headers = new Headers(init.headers || {});
  if (_jwt) headers.set('Authorization', `Bearer ${_jwt}`);
  return fetch(input, { ...init, headers });
};

export const aws = new PostgrestClient(url, {
  headers: { 'x-moreece-app': apiKey },
  fetch: jwtFetch,
});

// No-op for source-compatibility with lib/supabase.js's setActor().
// Real PIN-aware writes are handled via the authorised-write path
// (currently a Supabase Edge Function; will become an AWS Lambda in Step 9).
export async function setActor() {}
